import { isValidElement, ReactNode } from 'react';
import {
  Notifications as MantineNotifications,
  notifications as mantineNotifications,
  NotificationData,
  cleanNotifications,
  cleanNotificationsQueue,
  hideNotification,
  showNotification,
  updateNotification,
  updateNotificationsState,
} from '@mantine/notifications';
import { CheckIcon, CircleAlertIcon, CircleXIcon } from 'lucide-react';

type NotificationType = 'error' | 'info' | 'success' | 'warning';
type NotificationMessageObject = { title?: ReactNode; message: ReactNode };
type NotificationMessage = ReactNode | NotificationMessageObject;
type NotificationOptions = Omit<NotificationData, 'title' | 'message'>;

const getNotificationConfig = (type: NotificationType) =>
  ({
    error: { color: 'red', icon: <CircleXIcon size={18} /> },
    info: { color: 'blue' },
    success: { color: 'green', icon: <CheckIcon size={18} /> },
    warning: { color: 'yellow', icon: <CircleAlertIcon size={18} /> },
  })[type];

const notification = Object.fromEntries(
  (['error', 'info', 'success', 'warning'] as const).map((type) => [
    type,
    (messageOrOptions: NotificationMessage, options?: NotificationOptions) => {
      const { title, message } =
        typeof messageOrOptions === 'object' && !isValidElement(messageOrOptions)
          ? (messageOrOptions as NotificationMessageObject)
          : { title: undefined, message: messageOrOptions };

      mantineNotifications.show({
        title,
        message,
        ...getNotificationConfig(type),
        ...options,
      });
    },
  ]),
) as Record<
  NotificationType,
  (messageOrOptions: NotificationMessage, options?: NotificationOptions) => void
>;

export {
  MantineNotifications as Notifications,
  notification,
  cleanNotifications,
  cleanNotificationsQueue,
  hideNotification,
  showNotification,
  updateNotification,
  updateNotificationsState,
};
